// 日本の祝日 (self-contained, no external dependency). Valid roughly 1948–2099.
// Covers fixed holidays, ハッピーマンデー, 春分/秋分の日 (equinox formula),
// 振替休日 (substitute holidays, 1973+) and 国民の休日 (sandwiched days, 1986+).
//
// Usage:
//   holidayName(date | 'YYYY-MM-DD')  -> Japanese holiday name string | null
//   isHoliday(date | 'YYYY-MM-DD')    -> boolean

const toDate = (v) => {
  if (!v) return null;
  if (v instanceof Date) return isNaN(v.getTime()) ? null : v;
  if (typeof v === 'string') {
    const [y, m, d] = v.split('-').map((x) => parseInt(x, 10));
    if (!y || !m || !d) return null;
    const dt = new Date(y, m - 1, d);
    return isNaN(dt.getTime()) ? null : dt;
  }
  return null;
};

// nth (1-based) weekday of a month. weekday: 0=Sun..6=Sat.
function nthWeekday(year, month, weekday, nth) {
  const first = new Date(year, month - 1, 1).getDay();
  const offset = (weekday - first + 7) % 7;
  return 1 + offset + (nth - 1) * 7;
}

function vernalEquinox(year) {
  if (year >= 1980) return Math.floor(20.8431 + 0.242194 * (year - 1980) - Math.floor((year - 1980) / 4));
  return Math.floor(20.8357 + 0.242194 * (year - 1980) - Math.floor((year - 1980) / 4));
}
function autumnalEquinox(year) {
  if (year >= 1980) return Math.floor(23.2488 + 0.242194 * (year - 1980) - Math.floor((year - 1980) / 4));
  return Math.floor(23.2588 + 0.242194 * (year - 1980) - Math.floor((year - 1980) / 4));
}

// Primary (non-derived) holidays for a year -> { 'M-D': name }
function fixedHolidays(year) {
  const h = {};
  const set = (m, d, name) => { h[`${m}-${d}`] = name; };

  set(1, 1, '元日');
  if (year >= 2000) set(1, nthWeekday(year, 1, 1, 2), '成人の日');
  else if (year >= 1949) set(1, 15, '成人の日');

  if (year >= 1967) set(2, 11, '建国記念の日');

  if (year >= 2020) set(2, 23, '天皇誕生日');
  else if (year >= 1989 && year <= 2018) set(12, 23, '天皇誕生日');
  else if (year <= 1988) set(4, 29, '天皇誕生日');

  if (year >= 1949) set(3, vernalEquinox(year), '春分の日');

  if (year >= 2007) set(4, 29, '昭和の日');
  else if (year >= 1989) set(4, 29, 'みどりの日');

  set(5, 3, '憲法記念日');
  if (year >= 2007) set(5, 4, 'みどりの日');
  set(5, 5, 'こどもの日');

  if (year >= 2003) set(7, nthWeekday(year, 7, 1, 3), '海の日');
  else if (year >= 1996) set(7, 20, '海の日');

  if (year >= 2016) set(8, 11, '山の日');

  if (year >= 2003) set(9, nthWeekday(year, 9, 1, 3), '敬老の日');
  else if (year >= 1966) set(9, 15, '敬老の日');
  if (year >= 1948) set(9, autumnalEquinox(year), '秋分の日');

  if (year >= 2020) set(10, nthWeekday(year, 10, 1, 2), 'スポーツの日');
  else if (year >= 2000) set(10, nthWeekday(year, 10, 1, 2), '体育の日');
  else if (year >= 1966) set(10, 10, '体育の日');

  set(11, 3, '文化の日');
  set(11, 23, '勤労感謝の日');

  return h;
}

const _cache = {};

function holidayMap(year) {
  if (_cache[year]) return _cache[year];
  const fixed = fixedHolidays(year);
  const map = { ...fixed };
  const isFixed = (m, d) => !!fixed[`${m}-${d}`];

  // 国民の休日 — a weekday sandwiched between two holidays (1986+)
  if (year >= 1986) {
    for (let m = 1; m <= 12; m++) {
      const dim = new Date(year, m, 0).getDate();
      for (let d = 1; d <= dim; d++) {
        if (isFixed(m, d)) continue;
        const dt = new Date(year, m - 1, d);
        if (dt.getDay() === 0) continue;
        const prev = new Date(year, m - 1, d - 1);
        const next = new Date(year, m - 1, d + 1);
        if (isFixed(prev.getMonth() + 1, prev.getDate()) && isFixed(next.getMonth() + 1, next.getDate())) {
          map[`${m}-${d}`] = '国民の休日';
        }
      }
    }
  }

  // 振替休日 — first non-holiday day after a holiday that fell on Sunday (1973+)
  if (year >= 1973) {
    for (let m = 1; m <= 12; m++) {
      const dim = new Date(year, m, 0).getDate();
      for (let d = 1; d <= dim; d++) {
        if (isFixed(m, d)) continue;
        let k = 1;
        let found = false;
        while (true) {
          const p = new Date(year, m - 1, d - k);
          const pm = p.getMonth() + 1;
          const pd = p.getDate();
          if (p.getFullYear() === year && isFixed(pm, pd)) {
            if (p.getDay() === 0) { found = true; break; }
            k++;
            continue;
          }
          break;
        }
        if (found && !map[`${m}-${d}`]) map[`${m}-${d}`] = '振替休日';
      }
    }
  }

  _cache[year] = map;
  return map;
}

export function holidayName(value) {
  const d = toDate(value);
  if (!d) return null;
  return holidayMap(d.getFullYear())[`${d.getMonth() + 1}-${d.getDate()}`] || null;
}

export function isHoliday(value) {
  return !!holidayName(value);
}
